// Lovable Cloud-backed product catalog layer.

import { supabase } from "@/integrations/supabase/client";

export interface CatalogProduct {
  node: {
    id: string;
    title: string;
    description: string;
    descriptionHtml: string;
    handle: string;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: {
            amount: string;
            currencyCode: string;
          };
          compareAtPrice?: {
            amount: string;
            currencyCode: string;
          } | null;
          availableForSale: boolean;
          quantityAvailable?: number | null;
          image: {
            url: string;
            altText: string | null;
          } | null;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
          descriptionHtml?: {
            value: string;
          } | null;
          galleryUrls?: string[];
          seoTitle?: string | null;
          seoDescription?: string | null;
          sku?: string | null;
          gtin?: string | null;
          netWeightG?: number | null;
          lengthCm?: number | null;
          widthCm?: number | null;
          heightCm?: number | null;
          packaging?: string | null;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
    collections?: {
      edges: Array<{
        node: {
          handle: string;
        };
      }>;
    };
    seoTitle?: string | null;
    seoDescription?: string | null;
  };
}

interface DbVariant {
  id: string;
  product_id: string;
  variant_id: string;
  title: string;
  sku: string | null;
  price_cents: number;
  currency: string;
  image_url: string | null;
  options: Array<{ name: string; value: string }> | null;
  description_html: string | null;
  active: boolean;
  sort_order: number;
  gallery_urls?: string[] | null;
  compare_at_price_cents?: number | null;
  seo_title?: string | null;
  seo_description?: string | null;
  gtin?: string | null;
  net_weight_g?: number | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  packaging?: string | null;
}

interface DbProduct {
  id: string;
  handle: string;
  title: string;
  description: string | null;
  image_url: string | null;
  active: boolean;
  sort_order: number;
  seo_title?: string | null;
  seo_description?: string | null;
}

function centsToAmount(cents: number): string {
  return (cents / 100).toFixed(2);
}

function mapProductShape(
  product: DbProduct,
  variants: DbVariant[]
): CatalogProduct {
  const sortedVariants = [...variants].sort((a, b) => a.sort_order - b.sort_order);
  const minCents = sortedVariants.length
    ? Math.min(...sortedVariants.map((v) => v.price_cents))
    : 0;
  const currency = sortedVariants[0]?.currency ?? "USD";

  const optionMap = new Map<string, Set<string>>();
  for (const v of sortedVariants) {
    for (const opt of v.options ?? []) {
      if (!optionMap.has(opt.name)) optionMap.set(opt.name, new Set());
      optionMap.get(opt.name)!.add(opt.value);
    }
  }

  const productImages: Array<{ node: { url: string; altText: string | null } }> = [];
  if (product.image_url) {
    productImages.push({ node: { url: product.image_url, altText: product.title } });
  }
  const galleryUrls: string[] = (product as any).gallery_urls ?? [];
  for (const url of galleryUrls) {
    if (url && url !== product.image_url) {
      productImages.push({ node: { url, altText: product.title } });
    }
  }
  for (const v of sortedVariants) {
    if (v.image_url && v.image_url !== product.image_url && !galleryUrls.includes(v.image_url)) {
      productImages.push({ node: { url: v.image_url, altText: v.title } });
    }
  }

  return {
    node: {
      id: product.id,
      title: product.title,
      description: product.description ?? "",
      descriptionHtml: product.description ?? "",
      handle: product.handle,
      priceRange: {
        minVariantPrice: { amount: centsToAmount(minCents), currencyCode: currency },
      },
      images: { edges: productImages },
      variants: {
        edges: sortedVariants.map((v) => ({
          node: {
            id: v.variant_id,
            title: v.title,
            price: { amount: centsToAmount(v.price_cents), currencyCode: v.currency },
            compareAtPrice:
              v.compare_at_price_cents && v.compare_at_price_cents > v.price_cents
                ? { amount: centsToAmount(v.compare_at_price_cents), currencyCode: v.currency }
                : null,
            availableForSale: v.active,
            image: v.image_url
              ? { url: v.image_url, altText: v.title }
              : null,
            selectedOptions: v.options ?? [],
            descriptionHtml: v.description_html ? { value: v.description_html } : null,
            galleryUrls: v.gallery_urls ?? [],
            seoTitle: v.seo_title ?? null,
            seoDescription: v.seo_description ?? null,
            sku: v.sku ?? null,
            gtin: v.gtin ?? null,
            netWeightG: v.net_weight_g ?? null,
            lengthCm: v.length_cm ?? null,
            widthCm: v.width_cm ?? null,
            heightCm: v.height_cm ?? null,
            packaging: v.packaging ?? null,
          },
        })),
      },
      options: Array.from(optionMap.entries()).map(([name, values]) => ({
        name,
        values: Array.from(values),
      })),
      collections: { edges: [] },
      seoTitle: product.seo_title ?? null,
      seoDescription: product.seo_description ?? null,
    },
  };
}

async function loadProductsWithVariants(filter?: { handle?: string }): Promise<CatalogProduct[]> {
  let q = supabase.from("products").select("*").eq("active", true).order("sort_order");
  if (filter?.handle) q = q.eq("handle", filter.handle);

  const { data: products, error: pErr } = await q;
  if (pErr) {
    console.error("Failed to load products:", pErr);
    return [];
  }
  if (!products?.length) return [];

  const { data: variants, error: vErr } = await supabase
    .from("product_variants")
    .select("*")
    .eq("active", true)
    .in("product_id", products.map((p) => p.id));

  if (vErr) {
    console.error("Failed to load variants:", vErr);
    return [];
  }

  const variantsByProduct = new Map<string, DbVariant[]>();
  for (const v of (variants ?? []) as unknown as DbVariant[]) {
    if (!variantsByProduct.has(v.product_id)) {
      variantsByProduct.set(v.product_id, []);
    }
    variantsByProduct.get(v.product_id)!.push(v);
  }

  return (products as DbProduct[]).map((p) =>
    mapProductShape(p, variantsByProduct.get(p.id) ?? [])
  );
}

export async function fetchProducts(_first = 20, _query?: string): Promise<CatalogProduct[]> {
  return loadProductsWithVariants();
}

export async function fetchProductByHandle(handle: string): Promise<CatalogProduct["node"] | null> {
  const results = await loadProductsWithVariants({ handle });
  return results[0]?.node ?? null;
}

export interface CatalogCollection {
  id: string;
  title: string;
  description: string;
  handle: string;
  products: { edges: CatalogProduct[] };
}

export async function fetchCollections(_first = 10): Promise<CatalogCollection[]> {
  const products = await loadProductsWithVariants();
  if (products.length === 0) return [];
  return [
    {
      id: "all",
      title: "All Products",
      description: "",
      handle: "all",
      products: { edges: products },
    },
  ];
}

export interface CartItem {
  lineId: string | null;
  product: CatalogProduct;
  variantId: string;
  variantTitle: string;
  price: { amount: string; currencyCode: string };
  quantity: number;
  selectedOptions: Array<{ name: string; value: string }>;
}
